import { auth } from '@/firebase';

const AUTH_READY_TIMEOUT_MS = 5000;

export class RateLimitError extends Error {
  constructor() {
    super('You are making requests too quickly. Please try again in a minute.');
    this.name = 'RateLimitError';
  }
}

/**
 * Wrapper around fetch() that adds a Firebase Auth Bearer token.
 * Use for ALL Cloud Function calls.
 */
export async function authFetch(url: string, options: RequestInit = {}): Promise<Response> {
  // Firebase restores the session from IndexedDB asynchronously. Routes are
  // already gated on that restore (AuthGate in router.tsx), so this is belt-and-
  // braces for any caller outside the gate. Bounded because a blocked IndexedDB
  // read would otherwise hang here forever — never resolving, never rejecting,
  // so no caller's catch or finally would ever run.
  await Promise.race([
    auth.authStateReady(),
    new Promise(resolve => setTimeout(resolve, AUTH_READY_TIMEOUT_MS)),
  ]);
  const token = await auth.currentUser?.getIdToken();
  if (!token) throw new Error('Not authenticated');
  const res = await fetch(url, {
    ...options,
    headers: {
      ...(options.headers instanceof Headers
        ? Object.fromEntries(options.headers.entries())
        : options.headers),
      'Authorization': `Bearer ${token}`,
    },
  });
  if (res.status === 429) throw new RateLimitError();
  return res;
}
